// Copyright (c) 2020-2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import './FormView.scss';
import FormModel from "../../../../nor/ui/types/FormModel";
import Button from "../../../../nor/ui/components/button/Button";
import Icon from "../../../../nor/ui/components/icon/Icon";
import { ExternalLink, MembersIcon, TrashIcon, UndoIcon } from "../../../../assets/icon";
import {UNDO_HISTORY_LENGTH} from "../../../../constants/common";
import Form from "../../../../nor/ui/components/form/Form";
import {ChangeCallback, VoidCallback} from "../../../../nor/ts/interfaces/callbacks";
import FormBuilder from "../../../common/builder/form/FormBuilder";
import Tab from "../../../../nor/ui/components/tab/Tab";
import {SelectFieldItem} from "../../../../nor/ui/types/items/SelectFieldModel";
import FormAppClassName from "../../../../constants/FormAppClassName";
import FormService from "../../../../services/FormService";
import RepositoryEntry from "../../../../nor/ts/simpleRepository/types/RepositoryEntry";
import FormDTO from "../../../../nor/ui/types/FormDTO";
import FormValue from "../../../../nor/ui/types/FormValue";
import LogService from "../../../../nor/ts/LogService";
import Loader from "../../../../nor/ui/components/loader/Loader";
import { PUBLIC_URL } from '../../../../constants/form-ui-environment';
import { TextField } from '../../../../nor/ui/components/fields/text/TextField';
import TextAreaField from "../../../../nor/ui/components/fields/textArea/TextAreaField";
import AppRoute from "../../../../constants/AppRoute";
import RouteService from "../../../../nor/ui/services/RouteService";
import PipelineRunModel from "../../../../nor/pipeline/types/PipelineRunModel";
import PipelineService from "../../../../services/PipelineService";
import AgentPoolDTO from "../../../../nor/pipeline/dto/AgentPoolDTO";
import PipelineDTO from "../../../../nor/pipeline/dto/PipelineDTO";
import AgentService from "../../../../services/AgentService";
import ConfigurePipelineRun from "../../../common/pipeline/run/configure/ConfigurePipelineRun";
import FormSaveModal from "../../../../modals/FormSaveModal";
import { ObserverDestructor } from '../../../../nor/ts/Observer';
import MemberListModal from "../../../../modals/MemberListModal";
import RepositoryMember from "../../../../nor/ts/simpleRepository/types/RepositoryMember";
import JsonField from "../../../../nor/ui/components/fields/json/JsonField";
import { cloneJson, ReadonlyJsonAny } from "../../../../nor/ts/Json";

const LOG = LogService.createLogger('FormView');

export const FORM_SAVE_TIMEOUT = 200;

export enum FormViewPreviewTab {

    PREVIEW,
    MODEL,
    VALUE,
    RUN

}

export enum FormViewComponentState {

    UNLOADED,
    LOADING,
    LOADED,
    EDITING,
    SAVING,
    LOAD_ERROR,
    SAVE_ERROR,
    DESTROYED

}

export interface FormViewState {

    readonly formId           : string | undefined;
    readonly formVersion      : number;
    readonly formModel        : FormModel;
    readonly formValue        : FormValue;
    readonly formPreviewValue : FormValue;
    readonly formMemberList   : RepositoryMember[];
    readonly formModelHistory : FormModel[];
    readonly formRun          : PipelineRunModel | undefined;

    readonly prevFormVersion      ?: number;
    readonly prevFormModel        ?: FormModel;
    readonly prevFormValue        ?: FormValue;
    readonly prevFormRun          ?: PipelineRunModel | undefined;

    readonly previewTab       : FormViewPreviewTab;
    readonly componentState   : FormViewComponentState;

    readonly agentListLoaded : boolean;
    readonly agentList       : RepositoryEntry<AgentPoolDTO>[];

    readonly pipelineListLoaded : boolean;
    readonly pipelineList       : RepositoryEntry<PipelineDTO>[];

}

export interface FormViewProps {

    readonly id         : string;
    readonly hasSession : boolean;

}

export class FormView extends React.Component<FormViewProps, FormViewState> {

    private readonly _changeFormModelCallback      : ChangeCallback<FormModel>;
    private readonly _changePreviewValueCallback   : ChangeCallback<any>;
    private readonly _changeValueCallback          : ChangeCallback<any>;
    private readonly _undoModelCallback            : VoidCallback;
    private readonly _deleteFormCallback           : VoidCallback;
    private readonly _previewTabs                  : SelectFieldItem<FormViewPreviewTab>[];
    private readonly _changeTabCallback            : ChangeCallback<FormViewPreviewTab>;
    private readonly _changePipelineRunCallback    : ChangeCallback<PipelineRunModel>;
    private readonly _saveModalSubmitCallback      : VoidCallback;
    private readonly _saveModalCancelCallback      : VoidCallback;
    private readonly _openMembersCallback          : VoidCallback;

    private _componentState            : FormViewComponentState;
    private _saveTimeout               : any | undefined;
    private _pipelineListLoading       : boolean;
    private _agentListLoading          : boolean;
    private _saveModalSubmitListener   : ObserverDestructor | undefined;
    private _saveModalCancelListener   : ObserverDestructor | undefined;

    public constructor (props: FormViewProps) {

        super(props);

        this._componentState = FormViewComponentState.UNLOADED;

        this._pipelineListLoading = false;
        this._agentListLoading    = false;

        this._saveTimeout = undefined;

        const formValue = {};

        this.state = {
            formId: undefined,
            formModel: {
                title: '',
                items: []
            },
            formMemberList: [],
            formVersion: 0,
            formValue: formValue,
            formPreviewValue: cloneJson(formValue) as FormValue,
            formRun: undefined,
            formModelHistory: [],
            previewTab: FormViewPreviewTab.PREVIEW,
            componentState: this._componentState,

            pipelineList : [],
            pipelineListLoaded: false,
            agentList: [],
            agentListLoaded: false
        };

        this._previewTabs = [
            {
                label: 'Preview',
                value: FormViewPreviewTab.PREVIEW
            },
            {
                label: 'Model',
                value: FormViewPreviewTab.MODEL
            },
            {
                label: 'Value',
                value: FormViewPreviewTab.VALUE
            },
            {
                label: 'Pipeline',
                value: FormViewPreviewTab.RUN
            }
        ];

        this._changeFormModelCallback      = this._onChangeFormModel.bind(this);
        this._changePreviewValueCallback   = this._onChangePreviewValueModel.bind(this);
        this._changeValueCallback          = this._onChangeValueModel.bind(this);
        this._changeTabCallback            = this._onChangeTab.bind(this);
        this._undoModelCallback            = this._onUndoModel.bind(this);
        this._deleteFormCallback           = this._onDeleteForm.bind(this);
        this._changePipelineRunCallback    = this._changePipelineRun.bind(this);
        this._saveModalSubmitCallback      = this._onSaveModalSubmit.bind(this);
        this._saveModalCancelCallback      = this._onSaveModalCancel.bind(this);
        this._openMembersCallback          = this._onOpenMembers.bind(this);

    }

    public componentDidMount (): void {

        this._loadForm();
        this._loadPipelineList();
        this._loadAgentList();

    }

    public componentWillUnmount (): void {

        if (this._saveTimeout !== undefined) {

            clearTimeout(this._saveTimeout);
            this._saveTimeout = undefined;

            // FIXME: We maybe should open some a dialog to ask if user wants to save it
            // LOG.debug(`componentWillUnmount: Saving form now.`);
            // this._saveForm();

        }

        if (this._saveModalSubmitListener) {
            this._saveModalSubmitListener();
            this._saveModalSubmitListener = undefined;
        }

        if (this._saveModalCancelListener) {
            this._saveModalCancelListener();
            this._saveModalCancelListener = undefined;
        }

        if (FormSaveModal.isOpen()) {
            FormSaveModal.closeModal();
        }

        this._componentState = FormViewComponentState.DESTROYED;

    }

    public render () {

        const formModel = this.state.formModel;
        const valueModel = this.state.formValue;
        const valuePreviewModel = this.state.formPreviewValue;
        const formModelHistory = this.state.formModelHistory;

        const componentState = this.state.componentState;
        const isLoading = componentState === FormViewComponentState.LOADING;
        const isSaving  = componentState === FormViewComponentState.SAVING;

        const publicFormUrl = `${PUBLIC_URL}/forms/${encodeURIComponent(this.props.id)}`;

        return (
            <div className={FormAppClassName.FORM_VIEW}>

                <div className={FormAppClassName.FORM_VIEW + '-editor'}>

                    <FormBuilder
                        title={`Form v${this.state.formVersion}`}
                        model={formModel}
                        onChange={this._changeFormModelCallback}
                    >

                        <Button click={this._openMembersCallback}><Icon><MembersIcon /></Icon></Button>

                        {formModelHistory.length ? (
                            <Button click={this._undoModelCallback}><Icon><UndoIcon /></Icon></Button>
                        ): null}

                        {isSaving ? (
                            <Loader className={FormAppClassName.FORM_VIEW + '-editor-loader'} />
                        ) : null}

                    </FormBuilder>

                </div>

                <div className={FormAppClassName.FORM_VIEW + '-preview'}>

                    <div className={FormAppClassName.FORM_VIEW + '-preview-buttons'}>

                        <Button
                            className={FormAppClassName.FORM_VIEW + '-delete-form'}
                            click={this._deleteFormCallback}><Icon><TrashIcon /></Icon> Delete Form</Button>

                    </div>

                    <Tab
                        className={FormAppClassName.FORM_VIEW + '-preview-tabs'}
                        value={this.state.previewTab}
                        values={this._previewTabs}
                        change={this._changeTabCallback}
                    />

                    { this.state.previewTab === FormViewPreviewTab.PREVIEW ? (
                        <React.Fragment>

                            <h2>Form Preview</h2>

                            <Form
                                model={formModel}
                                value={valuePreviewModel}
                                change={this._changePreviewValueCallback}
                                submit={() => {}}
                                cancel={() => {}}
                            />

                            <TextField
                                label="URL"
                                value={publicFormUrl}
                                change={() => {}}
                            >
                                <a className={FormAppClassName.FORM_VIEW + '-external-link-icon'} href={publicFormUrl} target="_blank" rel="noreferrer"><Icon><ExternalLink /></Icon></a>
                            </TextField>

                            <TextAreaField
                                label="IFrame"
                                change={() => {}}
                                value={`<iframe src="${publicFormUrl}" style="border:0" width="800" height="600" hspace="0" vspace="0" allowfullscreen="true"></iframe>`}
                            />

                        </React.Fragment>
                    ) : null}


                    { this.state.previewTab === FormViewPreviewTab.MODEL ? (
                        <React.Fragment>

                            <h2>Form Model</h2>

                            <JsonField
                                className={FormAppClassName.FORM_VIEW + '-preview-textarea'}
                                value={formModel as unknown as ReadonlyJsonAny}
                                change={this._changeFormModelCallback as any}
                            />

                        </React.Fragment>
                    ) : null}

                    { this.state.previewTab === FormViewPreviewTab.VALUE ? (
                        <React.Fragment>

                            <h2>Form Value</h2>

                            <JsonField
                                className={FormAppClassName.FORM_VIEW + '-preview-textarea'}
                                value={valueModel}
                                change={this._changeValueCallback}
                            />

                            <p><strong>Warning!</strong><br />
                                Values defined here will be used as the initial value for the form -- and are<br />
                                available publicly to the end user!<br />
                            <strong>Do not define any secrets here.</strong></p>

                        </React.Fragment>
                    ) : null}

                    { this.state.previewTab === FormViewPreviewTab.RUN ? (
                        <React.Fragment>

                            <h2>Pipeline</h2>

                            <ConfigurePipelineRun
                                model={this.state.formRun}
                                pipelineList={this.state.pipelineList}
                                agentList={this.state.agentList}
                                change={this._changePipelineRunCallback}
                            />

                        </React.Fragment>
                    ) : null}

                </div>

                {isLoading ? (
                    <Loader className={FormAppClassName.FORM_VIEW + '-loader'} />
                ) : null}

            </div>
        );

    }


    private _setModel (model: FormModel) {

        let modelHistory = [
            this.state.formModel,
            ...this.state.formModelHistory
        ];

        if (modelHistory.length >= UNDO_HISTORY_LENGTH) {
            modelHistory = modelHistory.slice(0, UNDO_HISTORY_LENGTH);
        }

        this.setState({
            formModel: model,
            formModelHistory: modelHistory
        });

        this._openSaveModalLater();

    }

    private _onChangeFormModel (model: FormModel) {
        this._setModel(model);
    }

    private _onChangeValueModel (model: any) {

        this.setState({
            formValue        : cloneJson(model) as FormValue,
            formPreviewValue : cloneJson(model) as FormValue
        });

        this._openSaveModalLater();

    }

    private _onChangePreviewValueModel (model: any) {

        this.setState({
            formPreviewValue: cloneJson(model) as FormValue
        });

    }

    private _changePipelineRun (run: PipelineRunModel) {

        LOG.debug(`Changing pipeline run: `, run);

        this.setState({
            formRun: run
        });

        this._openSaveModalLater();

    }

    private _onChangeTab (tab: FormViewPreviewTab) {
        this.setState({
            previewTab: tab
        });
    }

    private _onUndoModel () {

        const [
            prevModel,
            ...modelHistory
        ] = this.state.formModelHistory;

        this.setState({
            formModel: prevModel,
            formModelHistory: modelHistory
        });

        this._openSaveModalLater();

    }

    private _loadForm () {

        if ( this._componentState === FormViewComponentState.LOADING
            || this._componentState === FormViewComponentState.SAVING
        ) {
            LOG.warn(`_loadForm: Already loading or saving form`);
            return;
        }

        LOG.debug(`_loadForm: Loading form now.`);

        this._componentState = FormViewComponentState.LOADING;

        this.setState({componentState: this._componentState});

        FormService.getFormForEditing(this.props.id).then((form : RepositoryEntry<FormDTO>) => {

            LOG.debug(`Form = `, form);

            const componentState = this._componentState = FormViewComponentState.LOADED;

            const formId      = form.id;
            const formVersion = form.version;
            const formModel   = form.data.model;
            const formValue   = form.data?.value ?? {};
            const formRun     = form.data?.run;
            const formMemberList = form?.members ?? [];

            this.setState({

                componentState   : componentState,
                formId           : formId,
                formVersion      : formVersion,
                formModel        : formModel,
                formValue        : formValue,
                formPreviewValue : cloneJson(formValue) as FormValue,
                formRun          : formRun,
                formMemberList   : formMemberList,

                prevFormVersion      : formVersion,
                prevFormModel        : formModel,
                prevFormValue        : formValue,
                prevFormRun          : formRun

            });

        }).catch(err => {

            this._componentState = FormViewComponentState.LOAD_ERROR;

            LOG.error(`Loading failed: `, err);

            // FIXME: Display error

            this.setState({
                componentState: this._componentState
            });

        });

    }

    private _saveNow () {

        if ( this._componentState === FormViewComponentState.LOADING
            || this._componentState === FormViewComponentState.SAVING
        ) {
            LOG.warn(`_saveForm: Already loading or saving form`);
            return;
        }

        LOG.debug(`_saveForm: Saving form now.`);

        this._componentState = FormViewComponentState.SAVING;

        this.setState({componentState: this._componentState});

        FormService.saveForm(
            this.props.id,
            this.state.formModel,
            this.state.formValue,
            this.state.formRun
        ).then((form : RepositoryEntry<FormDTO>) => {

            this._componentState = FormViewComponentState.LOADED;

            if (this._saveTimeout !== undefined) {

                LOG.info(`New save is pending.`);

            } else {

                LOG.info(`Save finished.`);

                const formVersion    = form.version;
                const formModel      = form.data.model;
                const formValue      = form.data?.value ?? {};
                const formRun        = form.data?.run;

                this.setState({
                    componentState : this._componentState,
                    formId         : form.id,

                    formVersion    : formVersion,
                    formModel      : formModel,
                    formValue      : formValue,
                    formPreviewValue : cloneJson(formValue) as FormValue,
                    formRun          : formRun,

                    prevFormVersion : formVersion,
                    prevFormModel   : formModel,
                    prevFormValue   : formValue,
                    prevFormRun     : formRun

                });

            }

        }).catch(err => {

            this._componentState = FormViewComponentState.SAVE_ERROR;

            LOG.error(`Failed to save form: `, err);

            // FIXME: Display error

            this.setState({
                componentState: this._componentState
            });

            this._openSaveModal();

        });

    }

    private _openSaveModal () {

        if ( this._componentState === FormViewComponentState.LOADING
            || this._componentState === FormViewComponentState.SAVING ) {
            LOG.debug(`_openSaveModal: Already loading or saving form`);
            this._openSaveModalLater();
            return;
        }

        if (this._saveModalSubmitListener) {
            this._saveModalSubmitListener();
            this._saveModalSubmitListener = undefined;
        }

        if (this._saveModalCancelListener) {
            this._saveModalCancelListener();
            this._saveModalCancelListener = undefined;
        }

        this._saveModalSubmitListener = FormSaveModal.on(FormSaveModal.Event.SAVE, this._saveModalSubmitCallback);
        this._saveModalCancelListener = FormSaveModal.on(FormSaveModal.Event.CANCEL, this._saveModalCancelCallback);

        FormSaveModal.openModal( this._componentState === FormViewComponentState.SAVE_ERROR );

    }

    private _openSaveModalLater () {

        if (this._saveTimeout !== undefined) {
            clearTimeout(this._saveTimeout);
            this._saveTimeout = undefined;
        }

        LOG.debug(`_openSaveModalLater: Triggered save modal later.`);

        this._saveTimeout = setTimeout( () => {

            this._saveTimeout = undefined;

            if ( this._componentState === FormViewComponentState.DESTROYED ) {
                LOG.debug(`_openSaveModalLater: Already destroyed`);
                return;
            }

            if ( this._componentState === FormViewComponentState.EDITING ) {
                LOG.debug(`_openSaveModalLater: Already in editing mode`);
                return;
            }

            if ( this._componentState === FormViewComponentState.LOADING
                || this._componentState === FormViewComponentState.SAVING ) {
                LOG.debug(`_openSaveModalLater: Already/still loading or saving form`);
                this._openSaveModalLater();
                return;
            }

            LOG.debug(`_openSaveModalLater: Moving in editing mode now.`);
            this._openSaveModal();

        }, FORM_SAVE_TIMEOUT)

    }

    private _onSaveModalSubmit () {

        this._saveNow();

    }

    private _onSaveModalCancel () {

        if (   this.state?.prevFormVersion !== undefined
            && this.state?.prevFormModel !== undefined
            && this.state?.prevFormValue !== undefined
        ) {

            LOG.debug(`Canceling to previous state ${this.state.prevFormVersion}`);

            this._componentState = FormViewComponentState.LOADED;

            this.setState({
                componentState   : this._componentState,
                formVersion      : this.state.prevFormVersion,
                formModel        : this.state.prevFormModel,
                formValue        : this.state.prevFormValue,
                formPreviewValue : cloneJson(this.state.prevFormValue) as FormValue,
                formRun          : this.state.prevFormRun,
                formModelHistory : []
            });

        } else {
            LOG.debug(`Cannot cancel: No previous state saved`);
        }

    }

    private _onDeleteForm () {

        // FIXME: Implement a spinner
        // FIXME: Implement a confirmation or undo modal

        const formId = this.props.id;
        FormService.deleteForm(formId).then(() => {

            LOG.info(`Removed form ${formId}`);

            RouteService.setRoute(AppRoute.FORM_LIST_VIEW);

        }).catch(err => {

            // FIXME: Display UI error
            LOG.error(`Could not delete form ${formId}: `, err);

        });

    }

    private _loadPipelineList () {

        if (!this.props.hasSession) {
            LOG.warn(`Warning! User has not logged in. Cannot load pipelines.`);
            return;
        }

        if (this._pipelineListLoading) {
            LOG.warn(`Warning! Page already loading.`);
            return;
        }

        this._pipelineListLoading = true;

        PipelineService.getPipelineList().then( (list) => {
            this._pipelineListLoading = false;
            LOG.debug(`Loaded list: `, list);
            this.setState({
                pipelineListLoaded: true,
                pipelineList: list
            });
        }).catch( (err) => {

            this._pipelineListLoading = false;

            LOG.error(`Error: `, err);

            // FIXME: Show error message

        });

    }

    private _loadAgentList () {

        if (!this.props.hasSession) {
            LOG.warn(`Warning! User has not logged in. Cannot load list.`);
            return;
        }

        if (this._agentListLoading) {
            LOG.warn(`Warning! Page already loading.`);
            return;
        }

        this._agentListLoading = true;

        AgentService.getAgentPoolList().then( (list) => {
            this._agentListLoading = false;
            LOG.debug(`Loaded agent list: `, list);
            this.setState({
                agentListLoaded: true,
                agentList: list
            });
        }).catch( (err) => {

            this._agentListLoading = false;

            LOG.error(`Error: `, err);

            // FIXME: Show error message

        });

    }

    private _onOpenMembers () {
        MemberListModal.openModal(this.state.formMemberList);
    }

}

export default FormView;
